import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { SCIMConfigurationRead, SCIMConfigurationUpdate, ScimService } from 'api';
import { Logger } from 'utilities';
import { GroupMapping } from './useSCIMConfiguration.types';

const useSCIMConfiguration = ({ firmId }: { firmId: number | null }) => {
  const [scimConfiguration, setScimConfiguration] = useState<SCIMConfigurationRead | null>(null);
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [groupMappings, setGroupMappings] = useState<GroupMapping[]>([]);

  const { enqueueSnackbar } = useSnackbar();

  const fetchSCIMConfiguration = useCallback(
    async (firmId: number) => {
      try {
        const response = await ScimService.apiScimConfigurationFirmRead(firmId.toString());
        setScimConfiguration(response);
      } catch (error) {
        Logger.error('Error fetching SCIM configuration:', error);
        enqueueSnackbar('Failed to fetch SCIM configuration.', { variant: 'error' });
        setScimConfiguration(null);
      }
    },
    [enqueueSnackbar]
  );

  const updateSCIMConfiguration = useCallback(
    async (firmId: number, data: SCIMConfigurationUpdate) => {
      try {
        const response = await ScimService.apiScimConfigurationUpdate(firmId.toString(), data);
        setScimConfiguration(response);
        enqueueSnackbar('SCIM configuration updated successfully.', { variant: 'success' });
      } catch (error) {
        Logger.error('Error updating SCIM configuration:', error);
        enqueueSnackbar('Failed to update SCIM configuration.', { variant: 'error' });
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    if (firmId !== null) {
      fetchSCIMConfiguration(firmId);
    }
  }, [firmId, fetchSCIMConfiguration]);

  useEffect(() => {
    if (scimConfiguration) {
      setIsEnabled(scimConfiguration.enabled!);
      setGroupMappings(
        scimConfiguration.group_mappings.map(mapping => ({
          idp_group_name: mapping.idp_group_name!,
          role: mapping.role,
          key: `${mapping.idp_group_name}-${mapping.role}`,
        }))
      );
    }
  }, [scimConfiguration]);

  return {
    scimConfiguration,
    isEnabled,
    setIsEnabled,
    groupMappings,
    setGroupMappings,
    fetchSCIMConfiguration,
    updateSCIMConfiguration,
  };
};

export default useSCIMConfiguration;
